/**
 * AWS Region information used for latency probing.
 * Each entry provides the EC2 endpoint used to measure
 * round-trip latency via DOM image error timing.
 *
 * Copyright 2025 ENERCALC, LLC
 * All rights reserved.
 */

/**
 * Information about an AWS region used for latency probing.
 */
export interface RegionInfo {
  /** Display name of the region (e.g., "California"). */
  readonly name: string;
  /** AWS region ID (e.g., "us-west-1"). */
  readonly id: string;
  /** EC2 endpoint URL for latency probing. */
  readonly endpoint: string;
}

/**
 * All AWS regions probed for latency on startup.
 * Typed as readonly to prevent runtime mutation.
 */
export const awsRegions: readonly RegionInfo[] = [
  { name: "California", id: "us-west-1", endpoint: "https://ec2.us-west-1.amazonaws.com/" },
  { name: "Oregon", id: "us-west-2", endpoint: "https://ec2.us-west-2.amazonaws.com/" },
  { name: "Virginia", id: "us-east-1", endpoint: "https://ec2.us-east-1.amazonaws.com/" },
  { name: "Ohio", id: "us-east-2", endpoint: "https://ec2.us-east-2.amazonaws.com/" },
  {
    name: "Canada Central",
    id: "ca-central-1",
    endpoint: "https://ec2.ca-central-1.amazonaws.com/",
  },
  { name: "Ireland", id: "eu-west-1", endpoint: "https://ec2.eu-west-1.amazonaws.com/" },
  { name: "London", id: "eu-west-2", endpoint: "https://ec2.eu-west-2.amazonaws.com/" },
  { name: "Paris", id: "eu-west-3", endpoint: "https://ec2.eu-west-3.amazonaws.com/" },
  { name: "Frankfurt", id: "eu-central-1", endpoint: "https://ec2.eu-central-1.amazonaws.com/" },
  { name: "Stockholm", id: "eu-north-1", endpoint: "https://ec2.eu-north-1.amazonaws.com/" },
  {
    name: "Middle East - Bahrain",
    id: "me-south-1",
    endpoint: "https://ec2.me-south-1.amazonaws.com/",
  },
  { name: "Hong Kong", id: "ap-east-1", endpoint: "https://ec2.ap-east-1.amazonaws.com/" },
  { name: "Mumbai", id: "ap-south-1", endpoint: "https://ec2.ap-south-1.amazonaws.com/" },
  { name: "Tokyo", id: "ap-northeast-1", endpoint: "https://ec2.ap-northeast-1.amazonaws.com/" },
  { name: "Seoul", id: "ap-northeast-2", endpoint: "https://ec2.ap-northeast-2.amazonaws.com/" },
  {
    name: "Osaka-Local",
    id: "ap-northeast-3",
    endpoint: "https://ec2.ap-northeast-3.amazonaws.com/",
  },
  {
    name: "Singapore",
    id: "ap-southeast-1",
    endpoint: "https://ec2.ap-southeast-1.amazonaws.com/",
  },
  { name: "Sydney", id: "ap-southeast-2", endpoint: "https://ec2.ap-southeast-2.amazonaws.com/" },
  { name: "Sao Paulo", id: "sa-east-1", endpoint: "https://ec2.sa-east-1.amazonaws.com/" },
  {
    name: "Beijing",
    id: "cn-north-1",
    endpoint: "https://ec2.cn-north-1.amazonaws.com.cn/",
  },
  {
    name: "Ningxia",
    id: "cn-northwest-1",
    endpoint: "https://ec2.cn-northwest-1.amazonaws.com.cn/",
  },
  {
    name: "AWS Gov Cloud East",
    id: "us-gov-east-1",
    endpoint: "https://ec2.us-gov-east-1.amazonaws.com/",
  },
  {
    name: "AWS Gov Cloud West",
    id: "us-gov-west-1",
    endpoint: "https://ec2.us-gov-west-1.amazonaws.com/",
  },
];
